import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useState } from 'react';
import { isSafari } from 'react-device-detect';
import { useBooth } from '../../../../../../hooks/useBooth';
import useMobileView from '../../../../../../hooks/useMobileView';
import { Booth } from '../../../../../../interfaces/interfaces';
import { BoothsComponentInterface, UiComponentElementInterface } from '../../../../../../interfaces/pagebuilder';
import { useSelector } from '../../../../../../reduxToolkit/typedRedux';
import { trackEvent, triggerOpenBoothTaskService } from '../../../../../../services/services';
import BoothModal from '../../../VBWShared/BoothModal';
import { BoothPopup } from '../../oneonone/BoothPopup';
import { NoBoothsFound } from './NoBoothsFound';
import { TitleRow } from './TitleRow';

export interface BoothsMinutes {
  duration: number;
  _id: {
    boothid: string;
  };
}
const BoothsUiComponent = ({ uiComponent }: UiComponentElementInterface<BoothsComponentInterface>) => {
  const event = useSelector((state) => state.common.event);
  const registration = useSelector((state) => state.common.eventRegistration);
  const user = useSelector((state) => state.common.user);
  const booths = useSelector((state) => state.common.booths);
  const templateEditMode = useSelector((state) => state.common.templateEditMode);
  const isMobilePreview = useSelector((state) => state.common.isMobilePreview);
  const router = useRouter();
  const { filterBooths, filterOnlineBooth } = useBooth();
  const { isMobileView } = useMobileView();

  useEffect(() => {
    if (router.query.openId && user && event && router.query.componentId === uiComponent?._id) {
      trackEvent('BOOTH_OPEN', { boothid: router.query.openId }, event?._id);
      if (event?.settings.allowAttendeeMessaging && registration) triggerOpenBoothTaskService(event._id, router.query.openId as string);
    }
  }, [router.query]);

  if (!event || !uiComponent) return <></>;

  const [searchPhrase, setSearchPhrase] = useState<string>();
  const [filterQuery, setFilterQuery] = useState<any>();
  const [showOnlyOnline, setOnlineFilter] = useState<boolean>(false);

  const filteredBooths: Booth[] = useMemo(
    () =>
      filterBooths(
        booths,
        searchPhrase,
        event.settings.showChildBooths,
        uiComponent?.showOnlyWithMeeting,
        uiComponent?.excludeFilters?.filter((ef) => ef.datetime_deleted === undefined).map((ef) => ef._id),
        uiComponent?.advancedFilters?.filter((af) => af.datetime_deleted === undefined).map((af) => af._id),
        filterQuery,
        uiComponent?.useRandomSort
      ),
    [booths, searchPhrase, filterQuery, uiComponent]
  );

  function getWidth(): string {
    if (!uiComponent) return '';
    return `${uiComponent.customStyling.width ? uiComponent.customStyling.width + (uiComponent.customStyling.isFixedWidth ? 'px' : '%') : '100%'}`;
  }

  function showBooth(booth: Booth, index: number) {
    if (uiComponent?.theme === 'mbw') return <BoothModal key={booth._id + index} booth={booth} customBackgroundColor={event?.template?.mainColor} />;
    return <BoothPopup key={booth._id + index} booth={booth} uiComponent={uiComponent} />;
  }

  return (
    <div
      className={`flex justify-center w-full`}
      style={{
        color: uiComponent?.styling?.textColor || '#000',
      }}>
      <div
        className="flex flex-col pb-8"
        style={{
          width: getWidth(),
          minWidth: '256px',
          maxWidth: '80cqw',
        }}>
        <TitleRow
          uiComponent={uiComponent}
          showOnlyOnlineBooths={showOnlyOnline}
          onShowOnlineBooths={(value) => setOnlineFilter(value)}
          onSearch={(query) => setSearchPhrase(query)}
          onFilter={(filter) => setFilterQuery(filter)}
        />
        <div>
          {filteredBooths && filteredBooths?.length > 0 ? (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${isMobileView() ? '100%' : 'auto-fill'}, 16rem)`,
                gap: '1rem',
              }}
              className={`${!isSafari ? 'gap-10' : ''} mt-8 justify-center @md:justify-between`}>
              {filteredBooths.filter((booth) => filterOnlineBooth(booth, showOnlyOnline)).map((booth, index) => showBooth(booth, index))}
            </div>
          ) : (
            <NoBoothsFound />
          )}
        </div>
      </div>
      <style jsx>
        {`
          @media only screen and (max-width: 768px) {
            .grid {
              grid-template-columns: repeat(100%, 16rem);
              justify-content: center;
            }
          }

          .grid {
            grid-template-columns: repeat(auto-fill, 16rem);
            justify-content: space-between;
          }
        `}
      </style>
    </div>
  );
};

export default BoothsUiComponent;
